import { type GetUsersResponse } from "@mg/schemas/src/christo/catalyst";
import {
  EnterpriseProfileType,
  UserPermission,
} from "@mg/schemas/src/commons/user";
import {
  CaretDown,
  CheckCircle,
  MagnifyingGlass,
  PaperPlaneRight,
  Plus,
  Question,
  Trash,
  PencilSimple,
  ArrowClockwise,
} from "@phosphor-icons/react";
import {
  Button,
  Dialog,
  Flex,
  Heading,
  IconButton,
  Popover,
  Select,
  Table,
  Text,
  TextArea,
  TextField,
  Tooltip,
  Grid,
} from "@radix-ui/themes";
// eslint-disable-next-line import/named
import { createRoute, redirect } from "@tanstack/react-router";
import cx from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";

import { AvatarWithInitials } from "../../../components/AvatarWithInitials";
import { EditProfileDialog } from "../../../components/EditProfileDialog";
import { useUI } from "../../../contexts/ui";
import { errorAnalyticsPayload, useAnalytics } from "../../../utils/analytics";
import {
  requiresAuth,
  canManageUsers,
  canAccessPuntt,
  canAccessCreativeConnect,
  canAccessEditOwnProfile,
  canAccessLists,
} from "../../../utils/auth";
import { errorMessage } from "../../../utils/http";
import {
  useArchiveUser,
  useArchiveMeaningfulGigsUser,
  useInviteUser,
  useUserRoleMutation,
  useGetMeaningfulGigsUsers,
  useAddUserToEnterprise,
  useResendInvite,
} from "../../../utils/queries/users";
import { store } from "../../../utils/store";
import { useGetVisibleUsers } from "../../../utils/tldraw/comments";
import { authLayoutRoute } from "../../auth-layout/route";
import { listsRoute } from "../../lists/route";
import { myNetworkRoute } from "../../network/route";
import { ticketsRoute } from "../../tickets/route";
import { aboutMeEditRoute } from "../../userProfile/routes/AboutMe/editRoute";

export const usersRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "users",
  beforeLoad() {
    const canAccess = canManageUsers();

    if (!canAccess) {
      if (canAccessPuntt()) {
        throw redirect({
          to: ticketsRoute.to,
        } as any);
      }

      if (canAccessCreativeConnect()) {
        throw redirect({
          to: myNetworkRoute.to,
        });
      }

      if (canAccessEditOwnProfile()) {
        throw redirect({
          to: aboutMeEditRoute.to,
        });
      }

      if (canAccessLists()) {
        throw redirect({
          to: listsRoute.to,
        });
      }
    }

    requiresAuth();
  },
  component: Users,
});

function Users() {
  const userRoleMutation = useUserRoleMutation();
  const posthog = useAnalytics("Users");
  const { notify } = useUI();

  const [filters, setFilters] = useState<{ name: string }>({ name: "" });

  const { value: currentUser } = store.getState().auth;
  // This is not how we normally like to check for access, but it needs to
  // match the backend, and at the time of writing the backend doesn't support
  // feature flags yet, so we didn't take on that scope as part of this feature.
  const canArchive = currentUser?.permissions?.includes(
    UserPermission.CONNECT_TEAM,
  );

  // Check if user has access to any actions
  const hasAnyActionAccess = canArchive;

  const [addUserName, setAddUserName] = useState("");
  const [addUserEmail, setAddUserEmail] = useState("");
  const [addUserRole, setAddUserRole] = useState<
    EnterpriseProfileType | undefined
  >(currentUser?.role);
  const [addUserDropdownOpen, setAddUserDropdownOpen] = useState(false);
  const [bulkImportUserCsv, setBulkImportUserCsv] = useState("");
  const [isBulkImporting, setIsBulkImporting] = useState(false);

  const closeNewUserDialogRef = useRef<HTMLButtonElement>(null);
  const closeBulkImportUserDialogRef = useRef<HTMLButtonElement>(null);

  const {
    data: unsortedUsers,
    refetch,
    isPending,
    isRefetching,
  } = useGetVisibleUsers(false);
  const users = useMemo(() => {
    return unsortedUsers
      ?.filter(
        (user) =>
          !filters.name ||
          user.name?.toLowerCase().includes(filters.name.toLowerCase()) ||
          user.email?.toLowerCase().includes(filters.name.toLowerCase()),
      )
      .toSorted((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [unsortedUsers, filters.name]);

  // Get meaningful gigs users not in enterprise
  const {
    data: meaningfulGigsUsers,
    refetch: refetchMeaningfulGigsUsers,
    isPending: isMeaningfulGigsUsersPending,
    isRefetching: isMeaningfulGigsUsersRefetching,
  } = useGetMeaningfulGigsUsers();

  const archiveUserMutation = useArchiveUser();
  const archiveMeaningfulGigsUserMutation = useArchiveMeaningfulGigsUser();
  const inviteUserMutation = useInviteUser();
  const addUserToEnterpriseMutation = useAddUserToEnterprise();
  const resendInviteMutation = useResendInvite();

  const isLoading =
    isPending ||
    isRefetching ||
    archiveUserMutation.isPending ||
    inviteUserMutation.isPending;

  const isMeaningfulGigsUsersLoading =
    isMeaningfulGigsUsersPending ||
    isMeaningfulGigsUsersRefetching ||
    addUserToEnterpriseMutation.isPending ||
    archiveMeaningfulGigsUserMutation.isPending;

  const loader = (
    <Table.Row>
      <Table.Cell colSpan={4} className="text-center">
        Loading users&hellip;
      </Table.Cell>
    </Table.Row>
  );

  const meaningfulGigsUsersLoader = (
    <Table.Row>
      <Table.Cell colSpan={3} className="text-center">
        Loading meaningful gigs users&hellip;
      </Table.Cell>
    </Table.Row>
  );

  let allowedRoles: EnterpriseProfileType[] = [];
  const roles = [
    EnterpriseProfileType.CATALYST_REQUESTER,
    EnterpriseProfileType.OPS,
    EnterpriseProfileType.ADMIN,
    EnterpriseProfileType.ADMIN_WITH_AI,
  ];
  if (currentUser?.role === EnterpriseProfileType.MEANINGFUL_GIGS) {
    roles.push(EnterpriseProfileType.CATALYST_AI);
    roles.push(EnterpriseProfileType.MEANINGFUL_GIGS);
    allowedRoles = roles;
  } else if (
    currentUser?.role === EnterpriseProfileType.ADMIN ||
    currentUser?.role === EnterpriseProfileType.ADMIN_WITH_AI
  ) {
    allowedRoles = roles;
  }

  function handleAddUserToEnterprise(userId: string, userName: string) {
    addUserToEnterpriseMutation.mutate(
      { targetUserId: userId },
      {
        onSuccess: () => {
          refetch();
          refetchMeaningfulGigsUsers();
          notify({
            variant: "success",
            title: `${userName} successfully added to enterprise`,
            message: "User has been added to the enterprise.",
            leadingIcon: <CheckCircle color="rgb(var(--base-black))" />,
          });
        },
        onError: (error) => {
          notify({
            variant: "error",
            title: `Failed to add ${userName} to enterprise`,
            message: error.message,
          });
        },
      },
    );
  }

  function handleArchiveMeaningfulGigsUser(userId: string, userName: string) {
    archiveMeaningfulGigsUserMutation.mutate(userId, {
      onSuccess: () => {
        refetchMeaningfulGigsUsers();
        notify({
          variant: "success",
          title: `${userName} successfully archived`,
          message:
            "User has been archived and removed from the internal admin list.",
          leadingIcon: <CheckCircle color="rgb(var(--base-black))" />,
        });
      },
      onError: (error) => {
        notify({
          variant: "error",
          title: `Failed to archive ${userName}`,
          message: error.message,
        });
      },
    });
  }

  function friendlyRole(role?: EnterpriseProfileType) {
    if (!role) return "";
    if (role === EnterpriseProfileType.OPS) {
      return "Normal";
    }
    if (role === EnterpriseProfileType.CATALYST_REQUESTER) {
      return "Restricted";
    }
    if (role === EnterpriseProfileType.CATALYST_AI) {
      return "AI";
    }
    if (role === EnterpriseProfileType.ADMIN_WITH_AI) {
      return "Admin With AI";
    }
    return role
      .replace(/-/g, " ")
      .replace(
        /\w\S*/g,
        (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
      );
  }

  function saveNewUserHandler(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const startTime = performance.now();

    inviteUserMutation.mutate(
      {
        enterpriseId: currentUser?.enterpriseId as string,
        name: addUserName,
        email: addUserEmail,
        role: addUserRole!,
      },
      {
        onSuccess: (data) => {
          refetch();

          posthog.capture("user_invited", {
            userId: data?._id,
            userEmail: addUserEmail,
            userName: addUserName,
            userRole: addUserRole,
            durationSeconds: (performance.now() - startTime) / 1000,
          });

          closeNewUserDialogRef.current?.click();

          notify({
            variant: "success",
            title: `${addUserName} successfully invited`,
            message: "User has been invited to the platform.",
            leadingIcon: <CheckCircle color="rgb(var(--base-black))" />,
          });

          setAddUserName("");
          setAddUserEmail("");
          setAddUserRole(currentUser?.role);
        },
        onError: (error) => {
          posthog.capture("user_invited_error", {
            userEmail: addUserEmail,
            userName: addUserName,
            userRole: addUserRole,
            durationSeconds: (performance.now() - startTime) / 1000,
            ...errorAnalyticsPayload(error),
          });

          notify({
            variant: "error",
            title: `Failed to invite ${addUserName}`,
            message: error.message,
          });

          throw error;
        },
      },
    );
  }

  function roleSelector() {
    return (
      // eslint-disable-next-line jsx-a11y/label-has-associated-control
      <label>
        <Text as="div" size="2" mb="1" weight="bold">
          Role
        </Text>
        <Select.Root
          value={addUserRole}
          onValueChange={(value) => {
            setAddUserRole(value as EnterpriseProfileType);
          }}
        >
          <Select.Trigger className="w-full">
            <Text as="span" size="1" className="capitalize">
              {friendlyRole(addUserRole)}
            </Text>
          </Select.Trigger>
          <Select.Content>
            {(allowedRoles.length ? allowedRoles : [currentUser?.role]).map(
              (role) => (
                <Select.Item
                  key={role}
                  value={role as string}
                  className="capitalize data-[highlighted]:bg-base-black data-[highlighted]:text-base-white"
                >
                  {friendlyRole(role)}
                </Select.Item>
              ),
            )}
          </Select.Content>
        </Select.Root>
      </label>
    );
  }

  function newUserForm() {
    return (
      <form onSubmit={saveNewUserHandler}>
        <Flex direction="column" gap="3">
          <label htmlFor="new-user-name">
            <Text as="div" size="2" mb="1" weight="bold">
              Name
            </Text>
            <TextField.Root
              id="new-user-name"
              placeholder="Enter name"
              value={addUserName}
              onChange={(e) => setAddUserName(e.target.value)}
              required
            />
          </label>
          <label htmlFor="new-user-email">
            <Text as="div" size="2" mb="1" weight="bold">
              Email
            </Text>
            <TextField.Root
              id="new-user-email"
              placeholder="Enter email"
              value={addUserEmail}
              onChange={(e) => setAddUserEmail(e.target.value)}
              required
            />
          </label>
          {roleSelector()}
        </Flex>
        <Flex gap="2" mt="4" justify="end">
          <Dialog.Close
            data-testid="add-user-cancel"
            onClick={(e) => e.stopPropagation()}
            ref={closeNewUserDialogRef}
          >
            <Button variant="soft" color="gray">
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            type="submit"
            data-testid="add-user-confirm"
            disabled={inviteUserMutation.isPending}
            loading={inviteUserMutation.isPending}
          >
            <PaperPlaneRight />
            Send Invitation
          </Button>
        </Flex>
      </form>
    );
  }

  async function bulkImportUserHandler(
    event: React.FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();
    const startTime = performance.now();
    setIsBulkImporting(true);
    setAddUserDropdownOpen(false);

    const csvRows = bulkImportUserCsv.trim().split("\n");
    if (!csvRows.length) {
      notify({
        variant: "error",
        title: "No users to import",
        message: "Please enter a list of users to import.",
      });
      return;
    }

    const domains = new Set(
      users?.map((user) => user.email?.split("@")[1]) ?? [],
    );

    // Error types
    const invalidRows = [];
    const newDomains = new Set<string>();
    const alreadyExists = [];
    const failed = [];

    const succeeded: string[] = [];
    for (const row of csvRows) {
      if (!row.trim()) continue;
      const [name, email] = row.split(",").map((part) => part.trim());
      if (!name && !email) continue;
      if (!name || !email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        invalidRows.push(row);
        continue;
      }
      if (!domains.has(email.split("@")[1])) {
        newDomains.add(email.split("@")[1]);
      }
      if (
        users?.some((user) => user.email === email) ||
        succeeded.includes(email)
      ) {
        alreadyExists.push(`${name} <${email}>`);
        continue;
      }

      try {
        // To avoid sending too many invites at once, we only send one at a time
        await inviteUserMutation.mutateAsync({
          enterpriseId: currentUser?.enterpriseId as string,
          name,
          email,
          role: addUserRole!,
        });
        succeeded.push(email);
      } catch (error) {
        const message = await errorMessage(error);
        failed.push(`${name} <${email}>: ${message}`);
      }
    }

    // It would be nice to show these all at once.
    if (failed.length) {
      notify({
        variant: "error",
        title: `Error inviting ${failed.length.toLocaleString()} user${failed.length === 1 ? "" : "s"}`,
        message: failed.join("\n"),
      });
    }
    if (invalidRows.length) {
      notify({
        variant: "error",
        title: `${invalidRows.length.toLocaleString()} invalid user${invalidRows.length === 1 ? "" : "s"}`,
        message: invalidRows.join("\n"),
      });
    }
    if (newDomains.size) {
      notify({
        variant: "default",
        title: `Users invited from ${newDomains.size.toLocaleString()} new domain${newDomains.size === 1 ? "" : "s"}`,
        message: Array.from(newDomains).join("\n"),
      });
    }
    if (alreadyExists.length) {
      notify({
        variant: "default",
        title: `${alreadyExists.length.toLocaleString()} user${alreadyExists.length === 1 ? "" : "s"} already added`,
        message: alreadyExists.join("\n"),
      });
    }
    // Show the success message last, otherwise people might see a green toast and leave without seeing the other messages.
    if (succeeded.length) {
      notify({
        variant: "success",
        title: `${succeeded.length.toLocaleString()} user${succeeded.length === 1 ? "" : "s"} successfully invited`,
        // Not showing `message: succeeded.join("\n"),` because it could be very long
        leadingIcon: <CheckCircle color="rgb(var(--base-black))" />,
      });
    }

    posthog.capture("bulk_import_users", {
      role: addUserRole,
      rows: csvRows.length,
      numSucceeded: succeeded.length,
      numFailed: failed.length,
      numInvalid: invalidRows.length,
      numNewDomains: newDomains.size,
      numAlreadyExists: alreadyExists.length,
      succeeded,
      failed,
      invalidRows,
      newDomains,
      alreadyExists,
      durationSeconds: (performance.now() - startTime) / 1000,
    });

    // Clean up
    refetch();
    setBulkImportUserCsv("");
    setAddUserRole(currentUser?.role);
    closeBulkImportUserDialogRef.current?.click();
    setIsBulkImporting(false);
  }

  function bulkImportUserForm() {
    return (
      <form onSubmit={bulkImportUserHandler}>
        <Flex direction="column" gap="3">
          {roleSelector()}
          <label htmlFor="bulk-import-csv">
            <Text as="div" size="2" mb="1" weight="bold">
              Name, Email
            </Text>
            <TextArea
              id="bulk-import-csv"
              placeholder={
                "First Last, name@example.com\nFirst Last, name@example.com"
              }
              value={bulkImportUserCsv}
              onChange={(e) => setBulkImportUserCsv(e.target.value)}
              required
            />
          </label>
        </Flex>
        <Flex gap="2" mt="4" justify="end">
          <Dialog.Close
            data-testid="bulk-import-cancel"
            onClick={(e) => e.stopPropagation()}
            ref={closeBulkImportUserDialogRef}
          >
            <Button variant="soft" color="gray">
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            type="submit"
            data-testid="bulk-import-confirm"
            disabled={inviteUserMutation.isPending || isBulkImporting}
            loading={inviteUserMutation.isPending || isBulkImporting}
          >
            <PaperPlaneRight />
            Send Invitations
          </Button>
        </Flex>
      </form>
    );
  }

  const [editUser, setEditUser] = useState<GetUsersResponse[number] | null>(
    null,
  );
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Reset invite user dialog fields when closed
  useEffect(() => {
    if (!addUserDropdownOpen) {
      setAddUserName("");
      setAddUserEmail("");
      setAddUserRole(currentUser?.role);
      setBulkImportUserCsv("");
    }
  }, [addUserDropdownOpen, currentUser?.role]);

  return (
    <div className={cx("mb-4 overflow-auto px-8 py-2")}>
      <Flex
        direction="row"
        align="center"
        justify="between"
        className="mb-2 px-2 py-4"
      >
        <Heading size="8" className="font-sans">
          User Management
          {users?.length ? ` (${users.length.toLocaleString()} users)` : ""}
        </Heading>
        <Flex align="center" gap="2">
          <TextField.Root
            placeholder="Search Users"
            size="1"
            defaultValue={filters.name}
            onInput={({ target }) =>
              setFilters({ name: (target as HTMLInputElement).value })
            }
          >
            <TextField.Slot>
              <MagnifyingGlass />
            </TextField.Slot>
          </TextField.Root>
          <Popover.Root
            open={addUserDropdownOpen}
            onOpenChange={setAddUserDropdownOpen}
          >
            <Flex>
              <Dialog.Root>
                <Dialog.Trigger>
                  <Button variant="solid" size="1" className="rounded-r-none">
                    <Plus />
                    Invite User
                  </Button>
                </Dialog.Trigger>
                <Dialog.Content style={{ maxWidth: 350 }}>
                  <Dialog.Title>Invite New User</Dialog.Title>
                  <Dialog.Description>
                    Add a new user to your enterprise. They will receive an
                    email invitation to join.
                  </Dialog.Description>
                  {newUserForm()}
                </Dialog.Content>
              </Dialog.Root>
              <Popover.Trigger>
                <IconButton
                  size="1"
                  className="rounded-l-none border-0 border-l border-solid border-l-puntt-neutral-gray-10 transition-[border-radius] data-[state=open]:rounded-br-none"
                >
                  <CaretDown />
                </IconButton>
              </Popover.Trigger>
            </Flex>
            <Popover.Content
              align="end"
              sideOffset={0}
              className="rounded-sm rounded-tr-none p-0"
            >
              <Dialog.Root>
                <Dialog.Trigger>
                  <Button variant="solid" size="1" className="rounded-tr-none">
                    <Plus />
                    Bulk Invite Users
                  </Button>
                </Dialog.Trigger>
                <Dialog.Content style={{ maxWidth: 350 }}>
                  <Dialog.Title>Bulk Invite Users</Dialog.Title>
                  <Dialog.Description>
                    Invite multiple users at once. Each line should contain a
                    name and email, separated by a comma.
                  </Dialog.Description>
                  {bulkImportUserForm()}
                </Dialog.Content>
              </Dialog.Root>
            </Popover.Content>
          </Popover.Root>
        </Flex>
      </Flex>
      <Flex direction="column" className="px-2">
        <Text size="3" color="gray">
          Use this page to invite new users, assign or change their roles,
          archive users, and keep your team organized. You can search, filter,
          and edit user details.
          <br />
          <br />
          When users join through a shared file or folder, they start as
          Restricted. You can upgrade their role as needed. For advanced changes
          or support, contact Meaningful Gigs.
        </Text>
      </Flex>
      <Grid gap="4" px="2" py="4">
        <Table.Root variant="surface" className="w-full min-w-[300px]">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className="w-[200px]">
                <Flex align="center" justify="start" gap="1">
                  Role
                  <Tooltip
                    content={
                      <span>
                        <strong>Admin:</strong> Invite, archive, and change
                        roles. Full file access, can dismiss/resolve comments.
                        <br />
                        <strong>Normal:</strong> Access all files,
                        dismiss/resolve comments. Cannot manage users or change
                        roles.
                        <br />
                        <strong>Restricted:</strong> Only access shared files,
                        can resolve (not dismiss) comments, cannot manage users
                        or change roles.
                      </span>
                    }
                  >
                    <Question
                      size={16}
                      weight="fill"
                      className="text-gray-500 inline-block align-middle"
                    />
                  </Tooltip>
                </Flex>
              </Table.ColumnHeaderCell>
              {hasAnyActionAccess && (
                <Table.ColumnHeaderCell className="w-[200px]">
                  <Flex align="center" justify="start" gap="1">
                    Actions
                    <Tooltip
                      content={
                        <span>
                          <strong>Edit:</strong> Update user details or change
                          their role.
                          <br />
                          <strong>Archive:</strong> Deactivate a user. Users
                          cannot be invited again.
                        </span>
                      }
                    >
                      <Question
                        size={16}
                        weight="fill"
                        className="text-gray-500 inline-block align-middle"
                      />
                    </Tooltip>
                  </Flex>
                </Table.ColumnHeaderCell>
              )}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading && loader}
            {users?.map((user: GetUsersResponse[number]) => {
              const isMgUser =
                user.email?.endsWith("@meaningfulgigs.com") ||
                user.role === EnterpriseProfileType.MEANINGFUL_GIGS;
              return (
              <Table.Row
                key={user._id}
                className={
                  isMgUser
                    ? "bg-puntt-blue-1 hover:bg-puntt-blue-2"
                    : "bg-[#F9F9F9] hover:bg-carbon-50"
                }
              >
                <Table.Cell className="align-middle">
                  <AvatarWithInitials
                    avatar={user?.avatar}
                    name={user?.name}
                    size={8}
                    square
                  />
                  <Text weight="bold" className="pl-2">
                    {user.name}
                  </Text>
                </Table.Cell>
                <Table.Cell className="align-middle">{user.email}</Table.Cell>
                <Table.Cell className="align-middle capitalize">
                  {currentUser?.userID === user._id || !allowedRoles.length ? (
                    <Text size="2" className="capitalize">
                      {friendlyRole(user.role)}
                    </Text>
                  ) : (
                    <Select.Root
                      value={user.role}
                      disabled={userRoleMutation.isPending}
                      onValueChange={(newRole) => {
                        if (user.role !== newRole) {
                          posthog.capture("user_role_updated", {
                            userId: user._id,
                            userEmail: user.email,
                            oldRole: user.role,
                            userRole: newRole,
                          });

                          userRoleMutation.mutate(
                            {
                              userId: user._id,
                              role: newRole as EnterpriseProfileType,
                            },
                            {
                              onSuccess: () => {
                                notify({
                                  variant: "success",
                                  title: "Role updated",
                                  message: "User role updated successfully.",
                                  leadingIcon: (
                                    <CheckCircle color="rgb(var(--base-black))" />
                                  ),
                                });
                                refetch();
                              },
                              onError: (error: any) => {
                                notify({
                                  variant: "error",
                                  title: "Role update failed",
                                  message:
                                    error?.message ||
                                    "Failed to update user role.",
                                });
                              },
                            },
                          );
                        }
                      }}
                    >
                      <Select.Trigger className="w-full">
                        <Text as="span" size="1" className="capitalize">
                          {friendlyRole(user.role)}
                        </Text>
                      </Select.Trigger>
                      <Select.Content>
                        {allowedRoles.map((role) => (
                          <Select.Item
                            key={role}
                            value={role}
                            className="capitalize data-[highlighted]:bg-base-black data-[highlighted]:text-base-white"
                          >
                            {friendlyRole(role)}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                  )}
                </Table.Cell>
                {hasAnyActionAccess && (
                  <Table.Cell className="align-middle">
                    {canArchive && (
                      <Flex gap="2" justify="start">
                        <Tooltip content="Edit profile">
                          <Button
                            variant="outline"
                            size="1"
                            onClick={() => {
                              setEditUser(user);
                              setIsEditDialogOpen(true);
                            }}
                            disabled={currentUser?.userID !== user._id}
                            data-testid={`edit-user-${user._id}`}
                          >
                            <PencilSimple size={14} />
                          </Button>
                        </Tooltip>

                        {/* Resend Invite Button - only show for users in INVITED status for current enterprise */}
                        {user.enterpriseProfiles?.find(
                          (profile) =>
                            profile.enterprise === currentUser?.enterpriseId &&
                            profile.onboarding === "invited",
                        ) && (
                          <Tooltip content="Resend invite">
                            <Button
                              variant="outline"
                              size="1"
                              onClick={() => {
                                const startTime = performance.now();
                                resendInviteMutation.mutate(user._id, {
                                  onSuccess: () => {
                                    refetch();
                                    posthog.capture("invite_resent", {
                                      userId: user._id,
                                      userEmail: user.email,
                                      userName: user.name,
                                      userRole: user.role,
                                      durationSeconds:
                                        (performance.now() - startTime) / 1000,
                                    });
                                    notify({
                                      variant: "success",
                                      title: `Invite resent to ${user.name}`,
                                      message:
                                        "A new invite link has been sent.",
                                      leadingIcon: (
                                        <CheckCircle color="rgb(var(--base-black))" />
                                      ),
                                    });
                                  },
                                  onError: (error) => {
                                    posthog.capture("invite_resent_error", {
                                      userId: user._id,
                                      userEmail: user.email,
                                      userName: user.name,
                                      userRole: user.role,
                                      durationSeconds:
                                        (performance.now() - startTime) / 1000,
                                      ...errorAnalyticsPayload(error),
                                    });
                                    notify({
                                      variant: "error",
                                      title: `Failed to resend invite to ${user.name}`,
                                      message: error.message,
                                    });
                                    throw error;
                                  },
                                });
                              }}
                              disabled={resendInviteMutation.isPending}
                              data-testid={`resend-invite-${user._id}`}
                            >
                              <ArrowClockwise size={14} />
                            </Button>
                          </Tooltip>
                        )}

                        <Dialog.Root>
                          <Dialog.Trigger>
                            <Tooltip content="Archive user">
                              <Button
                                variant="outline"
                                color="red"
                                size="1"
                                disabled={
                                  currentUser?.userID === user._id &&
                                  !user?.archivedAt
                                }
                                data-testid={`archive-user-${user._id}`}
                              >
                                <Trash size={14} />
                              </Button>
                            </Tooltip>
                          </Dialog.Trigger>
                          <Dialog.Content className="grid w-[90vw] max-w-lg gap-4">
                            <Dialog.Title>
                              Archive <em>{user.name}</em>
                            </Dialog.Title>
                            <Dialog.Description>
                              Are you sure you want to permanently archive this
                              user?
                            </Dialog.Description>
                            <Flex gap="3" justify="end">
                              <Dialog.Close>
                                <Button variant="soft" color="gray">
                                  Cancel
                                </Button>
                              </Dialog.Close>
                              <Button
                                variant="solid"
                                color="red"
                                onClick={() => {
                                  const startTime = performance.now();
                                  archiveUserMutation.mutate(user._id, {
                                    onSuccess: () => {
                                      refetch();
                                      posthog.capture("user_archived", {
                                        userId: user._id,
                                        userEmail: user.email,
                                        userName: user.name,
                                        userRole: user.role,
                                        durationSeconds:
                                          (performance.now() - startTime) /
                                          1000,
                                      });
                                      notify({
                                        variant: "success",
                                        title: `${user.name} successfully archived`,
                                        message: "User has been archived.",
                                        leadingIcon: (
                                          <CheckCircle color="rgb(var(--base-black))" />
                                        ),
                                      });
                                    },
                                    onError: (error) => {
                                      posthog.capture("user_archived_error", {
                                        userId: user._id,
                                        userEmail: user.email,
                                        userName: user.name,
                                        userRole: user.role,
                                        durationSeconds:
                                          (performance.now() - startTime) /
                                          1000,
                                        ...errorAnalyticsPayload(error),
                                      });
                                      notify({
                                        variant: "error",
                                        title: `Failed to archive ${user.name}`,
                                        message: error.message,
                                      });
                                      throw error;
                                    },
                                  });
                                }}
                              >
                                <Trash />
                                Archive
                              </Button>
                            </Flex>
                          </Dialog.Content>
                        </Dialog.Root>
                      </Flex>
                    )}
                  </Table.Cell>
                )}
              </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </Grid>
      <EditProfileDialog
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) {
            setEditUser(null);
            refetch();
          }
        }}
        user={editUser}
      />

      {/* Meaningful Gigs Users Table */}
      {currentUser?.role === EnterpriseProfileType.MEANINGFUL_GIGS && (
        <Grid gap="4" px="2" py="4">
          <Heading size="6" className="font-sans">
            Internal: Available Meaningful Gigs Users
            {meaningfulGigsUsers?.length
              ? ` (${meaningfulGigsUsers.length.toLocaleString()} users)`
              : ""}
          </Heading>
          <Text size="3" color="gray">
            Internal admin tool: These @meaningfulgigs.com users are not yet in
            your enterprise. You can add them directly or archive unwanted
            accounts. Only visible to MEANINGFUL_GIGS users.
          </Text>
          <Table.Root variant="surface" className="w-full min-w-[300px]">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell className="w-1/2">
                  Name
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="w-1/2">
                  Email
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="w-0 text-right">
                  Actions
                </Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isMeaningfulGigsUsersLoading && meaningfulGigsUsersLoader}
              {meaningfulGigsUsers?.map((user) => (
                <Table.Row
                  key={user._id}
                  className="bg-[#F9F9F9] hover:bg-carbon-50"
                >
                  <Table.Cell className="w-1/2 align-middle">
                    <AvatarWithInitials
                      avatar={user?.avatar}
                      name={user?.name}
                      size={8}
                      square
                    />
                    <Text weight="bold" className="pl-2">
                      {user.name}
                    </Text>
                  </Table.Cell>
                  <Table.Cell className="w-1/2 align-middle">
                    {user.email}
                  </Table.Cell>
                  <Table.Cell className="w-0 align-middle">
                    <Flex gap="2" className="flex-nowrap justify-end">
                      <Button
                        variant="solid"
                        className="shrink-0"
                        size="1"
                        onClick={() =>
                          handleAddUserToEnterprise(
                            user._id,
                            user.name || user.email || "User",
                          )
                        }
                        disabled={addUserToEnterpriseMutation.isPending}
                        loading={addUserToEnterpriseMutation.isPending}
                      >
                        <Plus />
                        Add to Enterprise
                      </Button>
                      <Button
                        variant="solid"
                        color="red"
                        className="shrink-0"
                        size="1"
                        onClick={() =>
                          handleArchiveMeaningfulGigsUser(
                            user._id,
                            user.name || user.email || "User",
                          )
                        }
                        disabled={archiveMeaningfulGigsUserMutation.isPending}
                        loading={archiveMeaningfulGigsUserMutation.isPending}
                      >
                        <Trash />
                        Archive User
                      </Button>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
              {meaningfulGigsUsers && meaningfulGigsUsers.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={3} className="text-gray-50 text-center">
                    No available meaningful gigs users to add
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Root>
        </Grid>
      )}
    </div>
  );
}
